import {
  ChangeDetectorRef,
  computed,
  Directive,
  effect,
  HostBinding,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Provider,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlName,
  FormControlStatus,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgModel,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Nullable } from 'primeng/ts-helpers';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 * @description
 * Directive includes the main functionality of a custom control
 *
 * -  Provided approach of a custom control creation is different then an usual method with an implementation of "writeValue", "registerOnChange", etc.,
 *    because we need an instance of FormControl has been provided to an internal control-component element (a wrapped component).
 *    It help an internal control-component to handle different control changes and react into expected way (for example, valid<->invalid change, pristine<->dirty change, etc.).
 *
 * -  "providers: CoreControlDirective.coreControlProvider(<Component>)"
 *    Always provide the control accessor token and core control directive as a part of providers for a host element.
 */
@Directive({
  standalone: true,
  selector: '[ospCoreControl]',
})
export abstract class CoreControlDirective implements ControlValueAccessor, OnInit, OnDestroy {
  private injector = inject(Injector);
  private cdr = inject(ChangeDetectorRef);

  @Input()
  @HostBinding('id')
  id?: string;

  @Input() set formControl(control: FormControl | AbstractControl | undefined) {
    if (control) {
      this.processFormControlDirective(control as FormControl);
    }
  }

  @Input() set formControlName(name: string | undefined) {
    if (name) {
      this.processFormControlNameDirective(name);
    }
  }

  // TODO: rename this.control -> this.$control
  public control = signal(new FormControl());
  protected onTouch: () => void = () => { };
  private ngModelValueChangeSubs: Subscription | undefined;

  $controlValue = signal<unknown>(undefined);
  $controlStatus = signal<FormControlStatus | undefined>(undefined);
  $controlDisabled = computed(() => Boolean(this.$controlStatus() === 'DISABLED'));
  isPristine$ = new BehaviorSubject(false);
  isRequired$ = new BehaviorSubject(false);

  private controlValueSubscription = new Subscription();
  private controlStatusSubscription = new Subscription();

  constructor() {
    effect(
      () => {
        this.subscribeToValueChange(this.control());
        this.subscribeToStatusChange(this.control());
        this.patchControlStandardMethods(this.control());
      },
      { allowSignalWrites: true },
    );
  }

  static coreControlProvider(component: unknown): Provider[] {
    return [
      {
        provide: NG_VALUE_ACCESSOR,
        multi: true,
        useExisting: component,
      },
      {
        provide: CoreControlDirective,
        useExisting: component,
      },
    ];
  }

  ngOnInit() {
    this.processNgModelDirective();
  }

  ngOnDestroy() {
    this.ngModelValueChangeSubs?.unsubscribe();
  }

  markForCheck(): void {
    this.cdr.markForCheck();
  }

  private getNgControl(): NgControl | null {
    return this.injector.get(NgControl, null, { self: true, optional: true });
  }

  /**
   * @description
   * Extract a control for the 'ngModel' using case
   */
  private processNgModelDirective(): void {
    const ngControl = this.getNgControl();
    if (!ngControl || !(ngControl instanceof NgModel)) {
      return;
    }

    this.control.set(ngControl.control);
    this.ngModelValueChangeSubs?.unsubscribe();
    this.ngModelValueChangeSubs = ngControl.control.valueChanges.subscribe((value) => {
      if (ngControl.viewModel !== value) {
        ngControl.viewToModelUpdate(value);
      }
    });
  }

  /**
   * @description
   * Extract a control for the 'formControl' using case
   */
  private processFormControlDirective(control: FormControl): void {
    if (!control || this.control() === control) {
      return;
    }
    this.control.set(control);
  }

  /**
   * @description
   * Extract a control for the 'formControlName' using case
   */
  private processFormControlNameDirective(name: string): void {
    const ngControl = this.getNgControl();
    if (!(ngControl instanceof FormControlName)) {
      return;
    }
    const form = this.injector.get(ControlContainer).control as FormGroup;
    const control = form?.controls?.[name] as FormControl;
    if (control) {
      this.control.set(control);
    }
  }

  private subscribeToValueChange(control: FormControl | Nullable): void {
    this.controlValueSubscription?.unsubscribe();
    if (control) {
      this.controlValueSubscription = control.valueChanges.subscribe((value) => this.$controlValue.set(value));
      this.$controlValue.set(control.value);
    } else {
      this.$controlValue.set(undefined);
    }
  }

  private subscribeToStatusChange(control: FormControl | Nullable): void {
    this.controlStatusSubscription?.unsubscribe();
    if (control) {
      this.controlStatusSubscription = control.statusChanges.subscribe((value) => {
        this.$controlStatus.set(value);
        this.cdr.markForCheck();        // Change detection because a status css classes are not always applied to a within input element
      });
      this.$controlStatus.set(control.status);
    } else {
      this.$controlStatus.set(undefined);
    }
  }

  // We are forced to patch standard methods of AbstractControl
  // in order to handle specific events and propagate corresponding changes for our custom controls
  private patchControlStandardMethods(control: FormControl | Nullable): void {
    this.patchMarkAsDirty(control);
    this.patchMarkAsPristine(control);
    this.patchSetValidators(control);
    this.patchClearValidators(control);
  }

  private patchMarkAsDirty(control: FormControl | Nullable): void {
    if (control) {
      const markAsDirtyOrigin = control.markAsDirty;
      control.markAsDirty = () => {
        markAsDirtyOrigin.call(control);
        this.isPristine$.next(false);
        this.cdr.detectChanges();
      }
    }
  }

  private patchMarkAsPristine(control: FormControl | Nullable): void {
    if (control) {
      const markAsPristineOrigin = control.markAsPristine;
      control.markAsPristine = () => {
        markAsPristineOrigin.call(control);
        this.isPristine$.next(true);
        this.cdr.detectChanges();
      }
    }
  }

  private patchSetValidators(control: FormControl | Nullable): void {
    if (control) {
      const setValidatorsOrigin = control.setValidators;
      control.setValidators = (validators: ValidatorFn | ValidatorFn[]) => {
        setValidatorsOrigin.call(control, validators);
        this.isRequired$.next(this.makeValidatorsArray(validators).includes(Validators.required));
      }
    }
  }

  private patchClearValidators(control: FormControl | Nullable): void {
    if (control) {
      const clearValidatorsOrigin = control.clearValidators;
      control.clearValidators = () => {
        clearValidatorsOrigin.call(control);
        this.isRequired$.next(false);
      }
    }
  }

  private makeValidatorsArray(validators: ValidatorFn | ValidatorFn[]) {
    if (!validators) return [];
    return Array.isArray(validators) ? validators : [validators];
  }

  writeValue() { }
  registerOnChange() { }
  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }
}
